import { CARD_ACTIVATABLE_STATES, type CardState, type CardsSummary } from "@seagames/contracts";
import { StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangleIcon, CheckIcon, ChevronRightIcon, IdCardIcon, PrinterIcon } from "../../components/accreditation/icons";
import { LoadError } from "../../components/e-accreditation/bulk-sheets";
import { CARD_STATE_TONE, cardStatesParam, overviewNumbers, stateBar } from "../../components/e-accreditation/model";
import { CardStatesGuide } from "../../components/e-accreditation/parts";
import { EmptyBlock, Section } from "../../components/transport/parts";
import { cardsSummaryOptions } from "../../lib/api/e-accreditation";
import { useDefaultGamesId } from "../../lib/competition/games";
import { canActivateCards, canPrintCards } from "../../lib/e-accreditation/access";

/**
 * Overview (her `eOverview`, admin.js:1515): the stat strip, the card state
 * bar with its legend, and Needs attention — every number from ONE
 * `GET /cards/summary`, every line a link into Cards already filtered.
 * Her Requests cell and Recent activity are later slices (no requests, and
 * no card activity feed, yet).
 */
export const Route = createFileRoute("/_app/accreditation/e-accreditation/")({
	component: OverviewPage,
});

function OverviewPage() {
	const { t } = useTranslation("eAccreditation");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const gamesId = useDefaultGamesId(search.games_id);
	const summary = useQuery(cardsSummaryOptions(gamesId));

	if (summary.isError) return <LoadError error={summary.error} onRetry={() => void summary.refetch()} />;
	if (!summary.data)
		return (
			<div className="grid gap-[1rem]" aria-busy>
				<div className="skeleton h-[4.5rem] rounded-[0.625rem]" />
				<div className="skeleton h-[5.5rem] rounded-[0.625rem]" />
				<div className="skeleton h-[12rem] rounded-[0.625rem]" />
			</div>
		);

	const s = summary.data;
	const n = overviewNumbers(s);
	const cardsLink = (states: readonly CardState[]) => ({ games_id: search.games_id, card_state: cardStatesParam(states) });

	return (
		<div className="grid gap-[1.125rem]">
			<StatsStrip
				label={t("overview.statsLabel")}
				cells={[
					{ key: "total", label: t("overview.stats.cards"), value: n.total, note: t("overview.stats.cardsNote") },
					{ key: "active", label: t("overview.stats.active"), value: n.active, note: t("overview.stats.activeNote") },
					{
						key: "toActivate",
						label: t("overview.stats.toActivate"),
						value: n.toActivate,
						note: (
							<Link to="/accreditation/e-accreditation/cards" search={cardsLink(CARD_ACTIVATABLE_STATES)} className="font-semibold text-gms-warn hover:underline">
								{canActivateCards(session) ? t("overview.stats.toActivateAction") : t("overview.stats.view")}
							</Link>
						),
					},
					{ key: "notPrinted", label: t("overview.stats.notPrinted"), value: n.notPrinted, note: t("overview.stats.notPrintedNote") },
					{ key: "lost", label: t("overview.stats.lost"), value: n.lost, note: t("overview.stats.lostNote") },
				]}
			/>

			<Section
				id="eacc-state-h"
				title={t("overview.stateTitle")}
				end={
					<span className="flex items-center gap-[0.5rem]">
						<CardStatesGuide size="sm" />
						<Link
							to="/accreditation/e-accreditation/cards"
							search={{ games_id: search.games_id }}
							className="inline-flex items-center gap-[0.125rem] text-[0.8125rem] font-semibold text-gms-navy hover:underline"
						>
							{t("overview.viewAll")}
							<ChevronRightIcon className="size-[0.875rem]" />
						</Link>
					</span>
				}
			>
				{s.total === 0 ? (
					<EmptyBlock icon={<IdCardIcon />} title={t("overview.emptyTitle")} hint={t("overview.emptyHint")} />
				) : (
					<StateBar summary={s} link={cardsLink} />
				)}
			</Section>

			<NeedsAttention summary={s} link={cardsLink} canActivate={canActivateCards(session)} canPrint={canPrintCards(session)} />
		</div>
	);
}

type CardsSearch = { games_id: string | undefined; card_state: string | undefined };

/** Her stacked bar + legend: one segment per state with cards, each legend entry a link to Cards filtered by it. */
function StateBar({ summary, link }: { summary: CardsSummary; link: (states: readonly CardState[]) => CardsSearch }) {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	const bar = stateBar(summary);
	return (
		<div className="grid gap-[0.75rem] px-[1rem] py-[0.875rem]">
			<div className="flex h-[0.75rem] gap-[2px] overflow-hidden rounded-full bg-gms-soft-2" role="img" aria-label={t("overview.barLabel")}>
				{bar.map((b) => (
					<span key={b.state} className="h-full first:rounded-l-full last:rounded-r-full" style={{ width: `${b.share * 100}%`, background: CARD_STATE_TONE[b.state].dot }} />
				))}
			</div>
			<ul className="flex flex-wrap gap-x-[1.125rem] gap-y-[0.375rem]">
				{bar.map((b) => (
					<li key={b.state}>
						<Link
							to="/accreditation/e-accreditation/cards"
							search={link([b.state])}
							className="inline-flex items-center gap-[0.375rem] text-[0.8125rem] text-gms-ink-2 no-underline hover:text-gms-navy"
						>
							<i aria-hidden className="block size-[0.625rem] rounded-full" style={{ background: CARD_STATE_TONE[b.state].dot }} />
							{t(`state.${b.state}`)}
							<b className="font-semibold text-gms-navy-ink tabular-nums">{fmt(b.count)}</b>
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}

type Todo = { key: string; n: number; icon: ReactNode; tone: "warn" | "bad" | "plain"; title: string; hint: string; action: string; states: readonly CardState[] };
const TONE = { warn: "bg-gms-warn-soft text-gms-warn", bad: "bg-gms-bad-soft text-gms-bad", plain: "bg-gms-navy-soft text-gms-navy" } as const;

/** Her "Needs attention": one line per thing with a count, each a link into Cards. */
function NeedsAttention({
	summary,
	link,
	canActivate,
	canPrint,
}: {
	summary: CardsSummary;
	link: (states: readonly CardState[]) => CardsSearch;
	canActivate: boolean;
	canPrint: boolean;
}) {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	const n = overviewNumbers(summary);
	const todos = (
		[
			{
				key: "toActivate",
				n: n.toActivate,
				icon: <CheckIcon />,
				tone: "warn",
				title: t("overview.attention.toActivate", { count: n.toActivate, n: fmt(n.toActivate) }),
				hint: canActivate ? t("overview.attention.toActivateHint") : t("overview.attention.toActivateHintNoc"),
				action: canActivate ? t("overview.attention.activate") : t("overview.attention.view"),
				states: CARD_ACTIVATABLE_STATES,
			},
			{
				key: "lost",
				n: n.lost,
				icon: <AlertTriangleIcon />,
				tone: "bad",
				title: t("overview.attention.lost", { count: n.lost, n: fmt(n.lost) }),
				hint: t("overview.attention.lostHint"),
				action: t("overview.attention.view"),
				states: ["LOST"],
			},
			{
				key: "notPrinted",
				n: n.notPrinted,
				icon: <PrinterIcon />,
				tone: "plain",
				title: t("overview.attention.notPrinted", { count: n.notPrinted, n: fmt(n.notPrinted) }),
				hint: canPrint ? t("overview.attention.notPrintedHint") : t("overview.attention.notPrintedHintNoc"),
				action: t("overview.attention.view"),
				states: ["NOT_PRINTED"],
			},
		] satisfies Todo[]
	).filter((x) => x.n > 0);

	return (
		<Section id="eacc-att-h" title={t("overview.attention.title")}>
			{todos.length === 0 ? (
				<EmptyBlock icon={<CheckIcon className="text-gms-ok" />} title={t("overview.attention.clear")} hint={t("overview.attention.clearHint")} />
			) : (
				<ul className="divide-y divide-gms-line">
					{todos.map((x) => (
						<li key={x.key}>
							<Link
								to="/accreditation/e-accreditation/cards"
								search={link(x.states)}
								className="flex items-center gap-[0.75rem] px-[0.875rem] py-[0.75rem] text-inherit no-underline transition-colors outline-none hover:bg-gms-soft focus-visible:bg-gms-navy-soft"
							>
								<span className={`grid size-[2.25rem] shrink-0 place-items-center rounded-[0.5rem] [&>svg]:size-[1.125rem] ${TONE[x.tone]}`}>{x.icon}</span>
								<span className="grid min-w-0 flex-1 gap-[0.125rem]">
									<b className="text-[0.84375rem] leading-[1.3] font-semibold text-gms-navy-ink">{x.title}</b>
									<span className="text-[0.75rem] text-gms-ink-3">{x.hint}</span>
								</span>
								<span className="inline-flex shrink-0 items-center gap-[0.125rem] text-[0.8125rem] font-semibold text-gms-navy">
									{x.action}
									<ChevronRightIcon className="size-[0.875rem]" />
								</span>
							</Link>
						</li>
					))}
				</ul>
			)}
		</Section>
	);
}
